<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import { useRoute } from 'vue-router'

import { type CalculationProgressInfo } from '@/components/calculation-result/type/type'
import {
  failureState,
  pendingState,
  progressState,
  successState
} from '@/components/calculation-loading/constants/progressState'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'

import { useCalculationStore } from '@/stores/calculation'

import ErrorCardLoading from '@/components/calculation-loading/ErrorCardLoading.vue'
import PendingCardLoading from '@/components/calculation-loading/PendingCardLoading.vue'
import StepProgressLinear from '@/components/calculation-loading/StepProgressLinear.vue'

const route = useRoute()

const emit = defineEmits(['updateCalculationStatus'])

const calculationStore = useCalculationStore()
const { currentItem } = storeToRefs(calculationStore)
const calculationProgressInfo = ref<CalculationProgressInfo>()

const steps = ref<[]>([])

let intervalId: number | undefined

const startPolling = async (calculationId: number, intervalMs = 1000) => {
  calculationProgressInfo.value = await calculationStore.getCalculationProgress(calculationId)
  intervalId = window.setInterval(async () => {
    calculationProgressInfo.value = await calculationStore.getCalculationProgress(calculationId)
  }, intervalMs)
}

const currentStepIndex = computed(() => {
  const message = calculationProgressInfo.value?.message
  if (!message) return -1
  return steps.value.findIndex((step) => step === message)
})

const getStepPercent = (index: number): number => {
  if (currentStepIndex.value === -1) return 0
  if (index < currentStepIndex.value) return 100
  if (index === currentStepIndex.value) return calculationProgressInfo.value?.percent ?? 0
  return 0
}

watch(
  calculationProgressInfo,
  (newValue) => {
    emit('updateCalculationStatus', newValue?.state)
  },
  { deep: true }
)

const calculationId = computed(() => {
  return parseInt(route.params.calculationId as string)
})

const navigateToProject = () => {
  if (currentItem.value?.project) {
    router.push({ name: 'project-detail', params: { id: currentItem.value.project } })
  }
}

const navigateToCalculationForm = () => {
  if (currentItem.value?.project && currentItem.value?.id) {
    router.push({
      name: 'calculation-form',
      params: { id: currentItem.value?.project, calculationId: currentItem.value?.id }
    })
  }
}

onMounted(async () => {
  if (calculationId.value) {
    steps.value = await calculationStore.getCalculationProgressSteps(calculationId.value)
    await startPolling(calculationId.value)
  }
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})

const isError = computed(() => {
  return (
    currentItem.value?.status === STATUS_CHOICES.ERROR ||
    calculationProgressInfo.value?.state === failureState
  )
})
</script>

<template>
  <template v-if="!currentItem">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>

  <v-container v-else max-width="850" class="py-10">
    <v-card elevation="0" flat>
      <template v-if="isError">
        <ErrorCardLoading
          class="mt-8"
          @back-to-project="navigateToProject"
          @back-to-form="navigateToCalculationForm"
        />
      </template>

      <template v-else>
        <v-card-title class="text-h5 text-center">
          Generating results for <strong>{{ currentItem?.name }}</strong
          >...
        </v-card-title>

        <template v-if="calculationProgressInfo?.state === pendingState">
          <PendingCardLoading class="mt-4" />
        </template>

        <template v-else>
          <v-card-subtitle class="text-center mb-2">
            Please wait while we process your calculation. This may take a few moments.
          </v-card-subtitle>

          <v-card-text class="mt-4">
            <StepProgressLinear
              v-for="(step, index) in steps"
              :key="index"
              :step="step"
              :is-current-step="index === currentStepIndex"
              :step-percent="getStepPercent(index)"
              :indeterminate="!getStepPercent(index) && index === currentStepIndex"
            ></StepProgressLinear>
          </v-card-text>
        </template>
      </template>
    </v-card>
  </v-container>
</template>
